import { calculateAge } from '@/modules/people/utils/calculate-age';
import roundToDecimalPoints from '@/modules/people/composables/round-to-decimal-points';
import { StatePayDetail } from '@/modules/people/store/employment';
import { JuniorRate } from '@/modules/people/types/employment';
import { JUNIOR_RATE_TYPE } from '@/modules/people/definitions/pay-rates';

// The most decimals validatePayRate accepts in a base rate.
const PAY_RATE_DECIMAL_PLACES = 4;

/**
 * Resolve the junior rate that applies to an employee's age.
 *
 * Mirrors EmploymentContractJuniorRateCollection::getByAge, the collection the
 * backend save and recalculate flows resolve against: an exact age match wins,
 * and an age below the youngest configured rate falls back to that rate.
 * Neither side clamps the upper bound — an adult must never resolve to a junior
 * rate. (JuniorRateCollection::getJuniorRateByAge does clamp both ends, but
 * only the DTOs' config fallback calls it, which no live flow reaches.)
 */
export function resolveJuniorRateForAge(
  juniorRates: JuniorRate[] | undefined | null,
  age: number | null | undefined
): JuniorRate | null {
  // A mistyped future date of birth yields a negative age, which would other-
  // wise read as "younger than the youngest" and earn a junior discount.
  if (age == null || age < 0 || !juniorRates?.length) {
    return null;
  }

  const exact = juniorRates.find(jr => jr.Age === age);
  if (exact) {
    return exact;
  }

  const youngest = juniorRates.reduce((a, b) => (b.Age < a.Age ? b : a));
  return age < youngest.Age ? youngest : null;
}

/**
 * True when the adult-rate override applies to a pay detail: the contract has a
 * junior row that resolves for the employee's age, so the checkbox is shown and
 * a choice can be persisted. An employee below the youngest configured bracket
 * resolves to that bracket, so the override applies to them too.
 *
 * Every surface that renders the checkbox and every path that persists the
 * choice must agree on this, or a decision gets saved for an employee who was
 * never offered one.
 */
export function juniorRateOverrideApplies(
  juniorRates: JuniorRate[] | undefined | null,
  employeeAge: number | null | undefined
): boolean {
  return resolveJuniorRateForAge(juniorRates, employeeAge) !== null;
}

/**
 * Initialize junior rate state on a pay detail.
 * Called from processAgreement, lazy-init, and both pay rate dropdown selection paths.
 *
 * @param payDetail - The pay detail state object to populate
 * @param juniorRates - Junior rates from the contract
 * @param baseRate - The contract's original base pay rule rate (adult rate before any mutation)
 * @param dob - Employee's date of birth string
 * @param juniorRateId - The existing JuniorRateId from the agreement (null for new selections)
 * @param overrideWithAdultRate - The explicit override flag from the agreement.
 *   When absent (undefined/null) the flag is inferred from juniorRateId, preserving
 *   behaviour for stale caches and older API responses that predate the field.
 */
export function initializeJuniorRateState(
  payDetail: Partial<StatePayDetail>,
  {
    juniorRates,
    baseRate,
    dob,
    juniorRateId = undefined,
    overrideWithAdultRate = undefined,
  }: {
    juniorRates: JuniorRate[] | undefined;
    baseRate: number | null | undefined;
    dob: string | null | undefined;
    juniorRateId?: number | null | undefined;
    overrideWithAdultRate?: boolean | null | undefined;
  }
): void {
  payDetail.juniorRates = juniorRates || [];
  payDetail.adultBaseRate = baseRate ?? null;
  // Same value, but this one is never rewritten afterwards: adultBaseRate tracks
  // whichever classification is in play (resolveAdultRateOnLevelChange rewrites it),
  // so only this can be reduced to get the "no classification" rate. Re-seeded here
  // rather than write-once so picking a different pay rate refreshes it; area groups
  // overwrite it with their own contract rate straight after this call.
  payDetail.adultDefaultBaseRate = baseRate ?? null;
  payDetail.employeeAge = calculateAge(dob);

  // If juniorRateId was provided from an existing agreement, use it as-is
  // (null means override was active, a number means junior rate was applied).
  // Only look up by age when no juniorRateId was provided (new pay rate selection).
  if (juniorRateId !== undefined) {
    payDetail.juniorRateId = juniorRateId ?? null;
  } else {
    const matchingJr = resolveJuniorRateForAge(payDetail.juniorRates, payDetail.employeeAge);
    payDetail.juniorRateId = matchingJr?.Id ?? null;
  }

  // Where the checkbox is not shown the override is not applicable — keep it
  // null (it must not be persisted as false, nor inferred as true). Where it is
  // shown, prefer the explicit flag from the agreement and only infer it from
  // JuniorRateId (set → not overridden, null → overridden) when it is absent.
  // That inference is valid only for agreement loads (juniorRateId provided):
  // on a fresh selection there is no saved choice, and an uninstalled
  // template's junior rows carry Id: null, so a null resolved id there must
  // default to unticked rather than read as an active override.
  if (!juniorRateOverrideApplies(payDetail.juniorRates, payDetail.employeeAge)) {
    payDetail.overrideWithAdultRate = null;
  } else if (overrideWithAdultRate != null) {
    payDetail.overrideWithAdultRate = overrideWithAdultRate;
  } else {
    payDetail.overrideWithAdultRate =
      juniorRateId === undefined ? false : payDetail.juniorRateId === null;
  }
}

/**
 * Resolve the adult rate after a level change, owning the stale-rate refresh:
 * when the level is cleared (selectedLevel is null/undefined), the pay
 * detail's adultBaseRate reflects whichever level was loaded and is now
 * stale, so it is rewritten to the saved "no level" rate here.
 * adultRateValue in common-pay-details-library-component reads it directly,
 * so it has to stay in sync. The bulk flow reaches this through the
 * bulk-actions store's resolveJuniorAdultRateOnLevelChange action, keeping
 * that store's writes visible in its own file.
 */
export function resolveAdultRateOnLevelChange(
  payDetail: Partial<StatePayDetail>,
  selectedLevel: { BaseRate: number } | null | undefined,
  savedNoneLevelSelectedBaseRate: number
): number {
  if (selectedLevel) {
    return selectedLevel.BaseRate;
  }
  payDetail.adultBaseRate = savedNoneLevelSelectedBaseRate;
  return savedNoneLevelSelectedBaseRate;
}

/**
 * Apply an adult-rate override change to a pay detail: record the choice and
 * keep juniorRateId consistent — restored to the age-matched junior rate when
 * the override is cleared, or taken from the caller when one is supplied.
 */
export function applyJuniorRateOverrideChange(
  payDetail: Partial<StatePayDetail>,
  overrideValue: boolean,
  juniorRateId: number | null | undefined = undefined
): void {
  payDetail.overrideWithAdultRate = overrideValue;
  if (!overrideValue) {
    // Restore juniorRateId to the rate resolved for the employee's age —
    // resolveJuniorRateForAge so an under-age employee clamps to the youngest
    // bracket, matching every other resolution path.
    const jr = resolveJuniorRateForAge(
      payDetail.juniorRates,
      payDetail.employeeAge
    );
    if (jr?.Id != null) {
      payDetail.juniorRateId = jr.Id;
    }
  } else if (juniorRateId != null) {
    payDetail.juniorRateId = juniorRateId;
  }
}

/**
 * Resolve the rate a junior row produces against an adult rate: the row's own
 * value for a per-hour rate, or the adult rate scaled by it for a multiplier.
 * Returns null when there is no rate to calculate.
 *
 * A multiplier rarely divides cleanly (17.47 x 0.55 = 9.6085000000000003), and
 * validatePayRate rejects a base rate with more than four decimals, so the
 * result is rounded to what the input accepts.
 */
export function calculateJuniorRateValue(
  adultRate: number,
  juniorRate: JuniorRate | null | undefined
): number | null {
  if (!juniorRate) {
    return null;
  }
  const value = parseFloat(juniorRate.Value);
  if (Number.isNaN(value)) {
    return null;
  }
  const rate =
    juniorRate.Type === JUNIOR_RATE_TYPE.MULTIPLIER ? adultRate * value : value;
  return roundToDecimalPoints(rate, PAY_RATE_DECIMAL_PLACES);
}

/**
 * Calculate the effective rate based on the adult rate and junior rate override state.
 * Returns the adult rate if override is checked, or the junior rate if unchecked.
 */
export function getEffectiveRate(
  adultRate: number,
  payDetail: Partial<StatePayDetail>
): number {
  if (payDetail.overrideWithAdultRate) {
    return adultRate;
  }
  const jr = resolveJuniorRateForAge(payDetail.juniorRates, payDetail.employeeAge);
  return calculateJuniorRateValue(adultRate, jr) ?? adultRate;
}
